/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  keyframes,
  IconButton,
  TextField,
  Tabs,
  Tab,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import T from 'i18n';
import api from 'common/api';
import { useSelector } from 'react-redux';
import { selectCurrentCaseId, selectUserId } from 'common/selectors';
import { useFetchAnnotations } from 'features/viewing/redux/fetchAnnotations';
import { HyperlinkRow } from '../../extraComponents/hyperlinks/HyperLinkRow';
import { ExistingHyperlinkRow } from '../../extraComponents/hyperlinks/ExistingHyperLinkRow';
import { Edit, EditOff, Undo } from '@mui/icons-material';
import { useUndoableList } from '../../hooks/useUndoableList';
import { Spinner } from 'features/common';

type HyperlinkCandidate = any;

type PreviewHyperlinksProps = {
  show: boolean;
  handleClose: () => void;
  selectedRows: any[];
  fromAllExternalIDs: boolean;
  fromAllDiscoveryIDs?: boolean;
  fromPageRefs: boolean;
  exhibitNumberHyperlinks?: boolean;
};

const underlineAnimation = keyframes`
  0% {
    text-decoration: underline;
  }
  75% {
    text-decoration: underline;
  }
  100% {
    text-decoration: none;
  }
`;
const useStyles = makeStyles({
  tableContainer: {
    maxHeight: '400px',
    overflowY: 'auto',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    borderBottom: '1px solid #ccc',
    textAlign: 'left',
    padding: '8px',
    position: 'sticky',
    top: 0,
    backgroundColor: '#f9f9f9',
    zIndex: 1,
  },
  td: {
    borderBottom: '1px solid #eee',
    padding: '8px',
  },
  existingTitle: {
    animation: `${underlineAnimation} 5s ease-in-out`,
  },
});

const PreviewHyperlinks: React.FC<PreviewHyperlinksProps> = ({
  show,
  handleClose,
  selectedRows,
  fromAllExternalIDs,
  fromPageRefs,
  fromAllDiscoveryIDs,
  exhibitNumberHyperlinks,
}) => {
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showExisting, setShowExisting] = useState(false);
  const [existingHyperlinks, setExistingHyperlinks] = useState<any[]>([]);
  const [existingCount, setExistingCount] = useState(0);

  const [isEditing, setIsEditing] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [copyFeedbackIndex, setCopyFeedbackIndex] = useState<number | null>(null);
  const [startPage, setStartPage] = useState(1);
  const [endPage, setEndPage] = useState(selectedRows?.[selectedRows.length - 1]?.pageCount || 1);
  const [pageError, setPageError] = useState<string | null>(null);
  const [createdByBreakdown, setCreatedByBreakdown] = useState<Record<string, number>>({});
  const [rawHyperlinks, setRawHyperlinks] = useState<any[]>([]);
  const [matchesNotFound, setMatchesNotFound] = useState<any[]>([]);
  const [ocrEval, setOCREval] = useState<{ validReferences: any[]; invalidReferences: any[] }>({
    validReferences: [],
    invalidReferences: [],
  });
  const [tabIndex, setTabIndex] = useState<number>(0);
  const [fetched, setFetched] = useState<boolean>(false);
  const currentUserId = useSelector(selectUserId);

  const { fetchAnnotations } = useFetchAnnotations();

  const classes = useStyles();

  const caseId = useSelector(selectCurrentCaseId);

  const file: any = selectedRows?.[selectedRows.length - 1];
  const {
    items: hyperlinks,
    setItems: setHyperlinks,
    handleDeleteRow,
    handleUndoDelete,
    undoStack,
  } = useUndoableList<any>([]);

  const fetchHyperlinks = async () => {
    if (!show) return;
    if (!file?.id || !caseId) {
      setFetchError('Missing file or case ID');
      return;
    }

    setFetched(false);
    setLoading(true);
    setFetchError(null);

    try {
      let url = `/cases/${caseId}/files/${file.id}/hyperlink-candidates?fromAllExternalIDs=${fromAllExternalIDs}&fromPageRefs=${fromPageRefs}&startPage=${startPage}&endPage=${endPage}&evaluate=true`;
      if (fromAllDiscoveryIDs) {
        url = `/cases/${caseId}/files/${file.id}/hyperlink-candidates?fromAllDiscoveryIDs=true&startPage=${startPage}&endPage=${endPage}&evaluate=true`;
      }
      if (exhibitNumberHyperlinks) {
        url = `/cases/${caseId}/files/${file.id}/hyperlink-candidates?fromAllExhibitNumbers=true&startPage=${startPage}&endPage=${endPage}&evaluate=true`;
      }
      const response: any = await api.get(url);
      let annotationsData: any[] = [];
      let matchesNotFoundData: any[] = [];
      let evalData: { validReferences: any[]; invalidReferences: any[] } = {
        validReferences: [],
        invalidReferences: [],
      };

      if ((response as any).candidates) {
        annotationsData = (response as any).candidates;
        matchesNotFoundData = (response as any).matchesNotFound || [];
        if ((response as any).eval) {
          evalData = {
            validReferences: (response as any).eval.validReferences || [],
            invalidReferences: (response as any).eval.invalidReferences || [],
          };
        }
      } else {
        annotationsData = response;
      }

      setRawHyperlinks(annotationsData);
      setMatchesNotFound(matchesNotFoundData);
      setOCREval(evalData);
      if (response.error) {
        setFetchError(
          T.translate('hyperlinks.preview.fetchError.generic', {
            defaultValue: 'An unknown error occurred, please try again later',
          }) as string,
        );
        setFetched(true);
        setLoading(false);
        return;
      }

      const annotations = annotationsData;

      const newHyperlinks: HyperlinkCandidate[] =
        annotations.length > 0
          ? annotations?.map((ann: any) => {
              return {
                sourcePage: ann.sourcePage,
                sourceText: ann.sourceText,
                targetDocId: ann.targetDocId,
                targetFilename: ann.targetFilename,
                targetPage: ann.targetPage,
                href: ann.href,
              };
            })
          : [];
      newHyperlinks.sort((a, b) => {
        if (a.sourcePage < b.sourcePage) {
          return -1;
        }
        if (a.sourcePage > b.sourcePage) {
          return 1;
        }
        return 0;
      });
      const filteredHyperlinks = newHyperlinks.filter(
        (link) =>
          link.sourcePage ||
          link.sourceText ||
          link.targetDocId ||
          link.targetFilename ||
          link.targetPage,
      );
      setHyperlinks(filteredHyperlinks);

      const byUser: Record<string, number> = {};
      newHyperlinks.forEach((h) => {
        if (h.createdBy) {
          byUser[h.createdBy] = (byUser[h.createdBy] || 0) + 1;
        }
      });
      setCreatedByBreakdown(byUser);

      setFetched(true);
      setLoading(false);
    } catch (err) {
      if (err instanceof Error) {
        setFetchError(err.message);
      } else {
        setFetchError(
          T.translate('hyperlinks.preview.fetchError.generic', {
            defaultValue: 'An unknown error occurred, please try again later',
          }) as string,
        );
      }
      setFetched(true);
      setLoading(false);
    }
  };

  const handleCreateHyperlinks = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      await api.post(
        `/cases/${caseId}/files/${file.id}/hyperlinks?overwriteExistingHyperlinks=${showExisting}`,
        rawHyperlinks,
      );
      handleClose();
    } catch (err) {
      if (err instanceof Error) {
        setFetchError(err.message);
      } else {
        setFetchError(
          T.translate('hyperlinks.preview.fetchError.generic', {
            defaultValue: 'An unknown error occurred, please try again later',
          }) as string,
        );
      }
      setLoading(false);
    }

    setLoading(false);
  };

  const getExisting = async () => {
    let existing = await fetchAnnotations({
      file: file.id,
    });
    existing.filter((ann: any) => ann.annotation.type === 'TextHyperlinkAnnotation');
    if (fromAllExternalIDs) {
      existing = existing.sort((a: any, b: any) => {
        return a.annotation.sourcePage - b.annotation.sourcePage;
      });
    } else {
      existing = existing.sort((a: any, b: any) => {
        return a.annotation.pageNumber - b.annotation.pageNumber;
      });
    }
    if (existing) {
      setExistingHyperlinks(existing);

      const existingHyperlinksCreatedByMe = existing.filter(
        (ann: any) => ann?.createdBy?.id === currentUserId,
      );
      setExistingCount(existingHyperlinksCreatedByMe.length);
    }
  };

  const clearAll = () => {
    setHyperlinks([]);
    setFetchError(null);
    setLoading(false);
    setShowExisting(false);
    setExistingHyperlinks([]);
    setIsEditing(false);
    setCreatedByBreakdown({});
    setRawHyperlinks([]);
  };

  const onClose = () => {
    clearAll();
    handleClose();
  };

  useEffect(() => {
    getExisting();
  }, [show, caseId, file]);

  useEffect(() => {
    if (copyFeedbackIndex !== null && copyFeedback) {
      const timer = setTimeout(() => {
        setCopyFeedback(null);
        setCopyFeedbackIndex(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [copyFeedbackIndex, copyFeedback]);

  const handleGenerateCandidates = () => {
    setPageError(null);

    // Basic validation
    if (startPage < 1 || endPage < 1) {
      setPageError(
        T.translate('hyperlinks.preview.pageError.greaterThanZero', {
          defaultValue: 'Page numbers must be greater than 0.',
        }),
      );
      return;
    }
    if (file?.pageCount && (startPage > file.pageCount || endPage > file.pageCount)) {
      setPageError(
        T.translate('hyperlinks.preview.pageError.maxPages', {
          defaultValue: 'Page numbers must be <= total pages ({{pageCount}}).',
          pageCount: file.pageCount,
        }),
      );
      return;
    }
    if (startPage > endPage) {
      setPageError(
        T.translate('hyperlinks.preview.pageError.startExceedEnd', {
          defaultValue: 'Start page cannot exceed end page.',
        }),
      );
      return;
    }

    // If valid, fetch candidates
    fetchHyperlinks();
  };

  return (
    <Dialog open={show} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {T.translate('hyperlinks.preview.title', {
          fileId: file?.id,
          defaultValue: `Preview Hyperlinks for {{fileId: file?.id}}`,
        })}
        {rawHyperlinks.length > 0 &&
          T.translate('hyperlinks.preview.totalCandidates', {
            count: rawHyperlinks.length,
            defaultValue: ` (Total: {{count: rawHyperlinks.length}}}`,
          })}
        <span style={{ display: 'flex', gap: '8px', flexDirection: 'row' }}>
          <IconButton onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? <EditOff sx={{ color: '#ECD474' }} /> : <Edit />}
          </IconButton>
          {undoStack?.length > 0 && (
            <IconButton
              onClick={handleUndoDelete}
              title={T.translate('hyperlinks.preview.undo', {
                defaultValue: 'Undo last deletion',
              })}
            >
              <Undo />
            </IconButton>
          )}
        </span>
      </DialogTitle>
      <DialogContent className={classes.tableContainer}>
        <Box display="flex" alignItems="center" gap={2} mb={2} mt={2}>
          <TextField
            label={T.translate('hyperlinks.preview.startPage', {
              defaultValue: 'Start Page',
            })}
            type="number"
            value={startPage}
            onChange={(e) => {
              setStartPage(Number(e.target.value));
            }}
            size="small"
          />
          <TextField
            label={T.translate('hyperlinks.preview.endPage', {
              defaultValue: 'End Page',
            })}
            type="number"
            value={endPage}
            onChange={(e) => {
              setEndPage(Number(e.target.value));
            }}
            size="small"
          />
          <Button
            variant="outlined"
            sx={{
              borderColor: '#748CEC',
              color: '#748CEC',
              '&:hover': { borderColor: '#748CEC' },
            }}
            onClick={handleGenerateCandidates}
          >
            {T.translate('hyperlinks.preview.generateCandidates', {
              defaultValue: 'Generate candidates',
            })}
          </Button>
        </Box>
        {pageError && (
          <Typography variant="body2" color="error" paragraph>
            {pageError}
          </Typography>
        )}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <Spinner />
          </Box>
        ) : fetchError ? (
          <Typography variant="body1" color="error">
            {fetchError}
          </Typography>
        ) : !fetched ? null : (
          <>
            <Tabs
              value={tabIndex}
              onChange={(_, newValue) => setTabIndex(newValue)}
              aria-label="hyperlinks-tabs"
              sx={{ '& .MuiTabs-indicator': { backgroundColor: '#748CEC' } }}
            >
              <Tab
                label="Candidates"
                sx={{ color: '#748CEC', '&.Mui-selected': { color: '#748CEC' } }}
              />
              <Tab
                label="Matches Not Found"
                sx={{ color: '#748CEC', '&.Mui-selected': { color: '#748CEC' } }}
              />
              <Tab
                label="OCR Eval"
                sx={{ color: '#748CEC', '&.Mui-selected': { color: '#748CEC' } }}
              />
            </Tabs>

            {tabIndex === 0 && (
              <>
                {hyperlinks.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    {T.translate('hyperlinks.preview.noData', {
                      defaultValue: 'No hyperlink candidates.',
                    })}
                  </Typography>
                ) : (
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        {isEditing && (
                          <th className={classes.th} style={{ width: '40px', textAlign: 'center' }}>
                            {T.translate('hyperlinks.preview.deleteColumnHeader', {
                              defaultValue: '',
                            })}
                          </th>
                        )}
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.sourcePageHeader', {
                            defaultValue: 'Source Page',
                          })}
                        </th>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.sourceTextHeader', {
                            defaultValue: 'Source Text',
                          })}
                        </th>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.targetdocIdHeader', {
                            defaultValue: 'Target Doc Id',
                          })}
                        </th>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.targetPageHeader', {
                            defaultValue: 'Target Page',
                          })}
                        </th>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.targetFilenameHeader', {
                            defaultValue: 'Target FileName',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {hyperlinks.map((link, index) => (
                        <HyperlinkRow
                          key={index}
                          link={link}
                          classes={classes}
                          handleDeleteRow={handleDeleteRow}
                          index={index}
                          isEditing={isEditing}
                        />
                      ))}
                    </tbody>
                  </table>
                )}
                {tabIndex === 0 && hyperlinks.length > 0 && !loading && (
                  <Box mt={2}>
                    <Typography variant="body2">
                      {T.translate('hyperlinks.preview.totalLinks', {
                        defaultValue: 'About to create {{totalLinks}} NEW hyperlinks.',
                        totalLinks: hyperlinks.length,
                      })}
                    </Typography>
                    {Object.keys(createdByBreakdown).length > 0 && (
                      <Box mt={1}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {T.translate('hyperlinks.preview.breakdownTitle', {
                            defaultValue: 'Breakdown by user',
                          })}
                        </Typography>
                        {Object.entries(createdByBreakdown).map(([user, count]) => (
                          <Typography key={user} variant="body2">
                            {`${user}: ${count}`}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                )}
                {tabIndex === 0 && existingHyperlinks.length > 0 && (
                  <Box mt={2}>
                    {showExisting && (
                      <Box mt={2}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'bold', mb: 1 }}
                          id="existingHyperlinksHeader"
                          className={classes.existingTitle}
                        >
                          {T.translate('hyperlinks.preview.existingTitle', {
                            defaultValue: 'Existing Hyperlinks',
                          })}
                        </Typography>
                        <table className={classes.table}>
                          <thead>
                            <tr>
                              <th className={classes.th}>
                                {T.translate('hyperlinks.preview.sourcePageHeader', {
                                  defaultValue: 'Source Page',
                                })}
                              </th>
                              <th className={classes.th}>
                                {T.translate('hyperlinks.preview.sourceTextHeader', {
                                  defaultValue: 'Source Text',
                                })}
                              </th>
                              <th className={classes.th}>
                                {T.translate('hyperlinks.preview.docIdHeader', {
                                  defaultValue: 'Source Doc Id.',
                                })}
                              </th>
                              <th className={classes.th}>
                                {T.translate('hyperlinks.preview.createdBy', {
                                  defaultValue: 'Created By',
                                })}
                              </th>
                              <th className={classes.th}>
                                {T.translate('hyperlinks.preview.linkPageHeader', {
                                  defaultValue: 'Source FileName',
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {existingHyperlinks.map((ann, idx) => (
                              <ExistingHyperlinkRow key={idx} ann={ann} classes={classes} />
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}

            {tabIndex === 1 && (
              <Box mt={2}>
                {matchesNotFound.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    {T.translate('hyperlinks.preview.noMissingMatches', {
                      defaultValue: 'No missing matches.',
                    })}
                  </Typography>
                ) : (
                  <>
                    <table className={classes.table}>
                      <thead>
                        <tr>
                          <th className={classes.th}>
                            {T.translate('hyperlinks.preview.missingReferenceHeader', {
                              defaultValue: 'Missing Reference',
                            })}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {matchesNotFound.map((m, idx) => (
                          <tr key={idx}>
                            <td className={classes.td}>{m}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </Box>
            )}

            {tabIndex === 2 && (
              <Box mt={2}>
                {ocrEval.validReferences.length === 0 ? (
                  <Typography variant="body1" color="textSecondary">
                    {T.translate('hyperlinks.preview.noValidReferences', {
                      defaultValue: 'No valid references found.',
                    })}
                  </Typography>
                ) : (
                  <table className={classes.table}>
                    <thead>
                      <tr>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.validReferenceHeader', {
                            defaultValue: 'Valid Reference',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ocrEval.validReferences.map((ref, idx) => (
                        <tr key={idx}>
                          <td className={classes.td}>{ref}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {ocrEval.invalidReferences.length === 0 ? (
                  <Typography variant="body1" color="textSecondary" sx={{ mt: 2 }}>
                    {T.translate('hyperlinks.preview.noInvalidReferences', {
                      defaultValue: 'No invalid references found.',
                    })}
                  </Typography>
                ) : (
                  <table className={classes.table} style={{ marginTop: 16 }}>
                    <thead>
                      <tr>
                        <th className={classes.th}>
                          {T.translate('hyperlinks.preview.invalidReferenceHeader', {
                            defaultValue: 'Invalid Reference',
                          })}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ocrEval.invalidReferences.map((ref, idx) => (
                        <tr key={idx}>
                          <td className={classes.td}>{ref}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {existingHyperlinks.length > 0 && (
          <Box display="flex" alignItems="center" alignSelf={'flex-start'} gap={1} mr="auto" ml={4}>
            <input
              type="checkbox"
              id="delete-hyperlinks"
              onChange={(e) => {
                setShowExisting(e.target.checked);
              }}
              checked={showExisting}
            />
            <label htmlFor="delete-hyperlinks" style={{ cursor: 'pointer', marginTop: 6 }}>
              {T.translate('hyperlinks.preview.deleteHyperlinks', {
                defaultValue: 'Delete my {{ existing }} existing hyperlinks?',
                existing: existingCount,
              })}
            </label>
          </Box>
        )}

        <Button onClick={onClose} sx={{ color: '#748CEC' }}>
          {T.translate('hyperlinks.preview.cancel', {
            defaultValue: 'Cancel',
          })}
        </Button>
        <Button
          variant="contained"
          sx={{ backgroundColor: '#748CEC' }}
          onClick={handleCreateHyperlinks}
          disabled={loading || hyperlinks.length === 0}
        >
          {T.translate('hyperlinks.preview.createButton', {
            defaultValue: 'Create',
          })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PreviewHyperlinks;
